import {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useConsultOrderStore} from '../../../store/useConsultOrder';
import {formatDate} from '../../../util/util';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowLeft, faCheck, faCopy, faTrash} from '@fortawesome/free-solid-svg-icons';
import {toast} from 'react-toastify';
import ConfirmDialog from '../../modal/ConfirmDialog';

const ConsultOrderDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderDetail, orderDetailError, getOrderDetail, deleteOrder, getOrderLoading } = useConsultOrderStore();

  // URL에서 orderId 추출: /consult-order/fix/{orderId} 또는 /consult-order/list/{orderId}
  const orderId = location.pathname.split('/')[3];

  const [copied, setCopied] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // 현재 경로가 fix인지 list인지 확인
  const isFromFix = location.pathname.includes('/consult-order/fix/');

  useEffect(() => {
    if (orderId) {
      getOrderDetail(orderId);
    }
  }, [getOrderDetail, location.pathname]);

  const handleBack = useCallback(() => {
    if (isFromFix) {
      navigate('/consult-order/fix');
    } else {
      navigate('/consult-order/list');
    }
  }, [navigate, isFromFix]);

  const handleCopyJson = useCallback(() => {
    if (orderDetail?.sajuResult) {
      const jsonString = JSON.stringify(orderDetail.sajuResult, null, 2);
      navigator.clipboard.writeText(jsonString).then(() => {
        setCopied(true);
        toast.success('JSON이 클립보드에 복사되었습니다.');
        setTimeout(() => setCopied(false), 2000);
      });
    }
  }, [orderDetail?.sajuResult]);

  const handleDelete = useCallback(() => {
    if (orderId) {
      deleteOrder(orderId, navigate);
    }
  }, [orderId, deleteOrder, navigate]);

  if (getOrderLoading) {
    return (
      <div className="!w-full !h-full !flex !items-center !justify-center">
        <div className="!text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (!orderDetail) {
    return (
      <div className="!w-full !h-full !flex !flex-col !items-center !justify-center !gap-4">
        <div className="!text-red-500 !font-medium">{orderDetailError || '데이터를 불러오지 못했습니다.'}</div>
        <button
          onClick={handleBack}
          className="!px-4 !py-2 !bg-gray-500 hover:!bg-gray-600 !text-white !rounded-lg !transition-colors"
        >
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  const isConfirmed = orderDetail.status?.code === 'CONFIRM';

  return (
    <div className="!w-full !h-full !flex !flex-col !overflow-auto">
      {/* 헤더 */}
      <div className="!flex !items-center !justify-between !py-4 !border-b !border-gray-200">
        <div className="!flex !items-center !gap-4">
          <button
            onClick={handleBack}
            className="!flex !items-center !gap-2 !px-3 !py-2 !text-gray-600 hover:!text-gray-800 !transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>목록으로</span>
          </button>
          <h1 className="!text-xl !font-bold">상담 주문 상세</h1>
        </div>
        {!isConfirmed && (
          <button
            onClick={() => setIsDeleteOpen(true)}
            className="!flex !items-center !gap-2 !px-4 !py-2 !bg-red-500 hover:!bg-red-600 !text-white !rounded-lg !transition-colors"
          >
            <FontAwesomeIcon icon={faTrash} />
            <span>삭제</span>
          </button>
        )}
      </div>

      <div className="!flex-1 !p-6 !overflow-auto">
        {/* 기본 정보 */}
        <div className="!bg-white !rounded-lg !border !border-gray-200 !p-6 !mb-6">
          <h2 className="!text-lg !font-semibold !mb-4">기본 정보</h2>
          <div className="!grid !grid-cols-2 md:!grid-cols-4 !gap-4">
            <div>
              <span className="!text-sm !text-gray-500">이름</span>
              <div className="!font-medium">{orderDetail.custInfo?.name || '-'}</div>
            </div>
            <div>
              <span className="!text-sm !text-gray-500">양/음력</span>
              <div className="!font-medium">{orderDetail.custInfo?.solarType || '-'}</div>
            </div>
            <div>
              <span className="!text-sm !text-gray-500">생년월일</span>
              <div className="!font-medium">
                {orderDetail.custInfo
                  ? `${orderDetail.custInfo.year}.${orderDetail.custInfo.month}.${orderDetail.custInfo.day}`
                  : '-'}
              </div>
            </div>
            <div>
              <span className="!text-sm !text-gray-500">생시</span>
              <div className="!font-medium">
                {orderDetail.custInfo?.hour != null && orderDetail.custInfo?.minute != null
                  ? `${String(orderDetail.custInfo.hour).padStart(2, '0')}:${String(orderDetail.custInfo.minute).padStart(2, '0')}`
                  : '-'}
              </div>
            </div>
            <div>
              <span className="!text-sm !text-gray-500">성별</span>
              <div className="!font-medium">{orderDetail.custInfo?.sexType || '-'}</div>
            </div>
            <div>
              <span className="!text-sm !text-gray-500">상태</span>
              <div className="!font-medium">
                <span
                  className={`!px-2 !py-1 !rounded !text-sm ${
                    isConfirmed
                      ? '!bg-green-100 !text-green-800'
                      : '!bg-yellow-100 !text-yellow-800'
                  }`}
                >
                  {orderDetail.status?.description || orderDetail.status?.code || '-'}
                </span>
              </div>
            </div>
            <div>
              <span className="!text-sm !text-gray-500">생성일</span>
              <div className="!font-medium">
                {orderDetail.createdAt ? formatDate(orderDetail.createdAt).timestamp : '-'}
              </div>
            </div>
            {orderDetail.confirmDt && (
              <div>
                <span className="!text-sm !text-gray-500">확정일</span>
                <div className="!font-medium">
                  {formatDate(orderDetail.confirmDt).timestamp}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 사주 결과 JSON - 확정 상태일 때만 표시 */}
        {isConfirmed && orderDetail.sajuResult && (
          <div className="!bg-white !rounded-lg !border !border-gray-200 !p-6">
            <div className="!flex !items-center !justify-between !mb-4">
              <h2 className="!text-lg !font-semibold">사주 결과 (JSON)</h2>
              <button
                onClick={handleCopyJson}
                className="!flex !items-center !gap-2 !px-4 !py-2 !bg-blue-500 hover:!bg-blue-600 !text-white !rounded-lg !transition-colors"
              >
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                <span>{copied ? '복사됨' : '결과 복사'}</span>
              </button>
            </div>
            <pre className="!bg-gray-900 !text-gray-100 !p-4 !rounded-lg !overflow-auto !text-sm !font-mono !max-h-[600px]">
              {JSON.stringify(orderDetail.sajuResult, null, 2)}
            </pre>
          </div>
        )}

        {isConfirmed && !orderDetail.sajuResult && (
          <div className="!bg-yellow-50 !border !border-yellow-200 !rounded-lg !p-6">
            <div className="!text-yellow-800">
              사주 결과를 계산할 수 없습니다. 입력된 정보를 확인해 주세요.
            </div>
          </div>
        )}

        {!isConfirmed && (
          <div className="!bg-blue-50 !border !border-blue-200 !rounded-lg !p-6">
            <div className="!text-blue-800">
              주문이 확정되면 사주 결과를 확인할 수 있습니다.
            </div>
          </div>
        )}
      </div>

      {/* 삭제 확인 다이얼로그 */}
      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        title="주문 삭제 확인"
        message="정말로 이 주문을 삭제하시겠습니까?
삭제된 주문은 복구할 수 없습니다."
        confirmText="삭제"
        cancelText="취소"
        type="danger"
      />
    </div>
  );
};

export default ConsultOrderDetail;
